import { useEffect } from 'react';
import {
  cancelAnimation,
  Easing,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';

export const LOAD_MAIN_EASING = {
  linear: Easing.linear,
  smooth: Easing.bezier(0.45, 0.05, 0.25, 1),
  soft: Easing.bezier(0.33, 1, 0.68, 1),
  snap: Easing.bezier(0.22, 1, 0.36, 1),
} as const;

export function useLoaderSpin(duration_ms: number): SharedValue<number> {
  const rotation = useSharedValue(0);

  useEffect(() => {
    rotation.value = 0;
    rotation.value = withRepeat(
      withTiming(360, { duration: duration_ms, easing: LOAD_MAIN_EASING.linear }),
      -1,
      false,
    );
    return () => cancelAnimation(rotation);
  }, [duration_ms, rotation]);

  return rotation;
}

export function useLoaderPulse(duration_ms: number): SharedValue<number> {
  const progress = useSharedValue(0);

  useEffect(() => {
    progress.value = 0;
    progress.value = withRepeat(
      withTiming(1, { duration: duration_ms, easing: LOAD_MAIN_EASING.soft }),
      -1,
      false,
    );
    return () => cancelAnimation(progress);
  }, [duration_ms, progress]);

  return progress;
}

export function useStaggeredWave(
  duration_ms: number,
  index: number,
  count: number,
  is_rtl: boolean,
): SharedValue<number> {
  const phase = useSharedValue(0);
  const order = is_rtl ? count - 1 - index : index;
  const delay_ms = (duration_ms / count) * order * 0.55;

  useEffect(() => {
    phase.value = 0;
    phase.value = withDelay(
      delay_ms,
      withRepeat(
        withSequence(
          withTiming(1, { duration: duration_ms * 0.38, easing: LOAD_MAIN_EASING.snap }),
          withTiming(0, { duration: duration_ms * 0.38, easing: LOAD_MAIN_EASING.soft }),
          withTiming(0, { duration: duration_ms * 0.24 }),
        ),
        -1,
        false,
      ),
    );
    return () => cancelAnimation(phase);
  }, [delay_ms, duration_ms, phase]);

  return phase;
}
